// Google Play Billing integration for TWA (Trusted Web Activity).
// On Android Play Store TWA, `window.getDigitalGoodsService` is available and we
// use it to trigger the native Play Billing flow via the W3C Payment Request API.
// On the web (desktop/browser), we fall back to Stripe.

import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Map our internal item identifiers to Play Console SKUs.
// We use a SINGLE consumable SKU for ALL one-time purchases (characters, levels,
// photos) — the backend decides what to actually unlock from item_type+item_id.
// Subscriptions have their own dedicated SKU.
// These MUST match the Product IDs you created in Play Console.
export const PLAY_SKU_ONE_TIME = 'mycompanionai_unlock';
export const PLAY_SKU_SUBSCRIPTION_MONTHLY = 'premium_monthly';

// Decoy SKUs ("full price"). These products MUST exist in Play Console at the
// *original* (non-discounted) price. They are NEVER offered for purchase — we
// only call `getDetails()` on them to read their price string. The real
// purchases keep going through the SKUs above (which carry the promo price).
// → 100% of pricing is now configured in Play Console; no code change needed
// to update prices or end the promotion.
export const PLAY_SKU_ONE_TIME_FULL = 'mycompanionai_unlock_full';
export const PLAY_SKU_SUBSCRIPTION_MONTHLY_FULL = 'premium_monthly_full';

export const buildPlaySku = (itemType, _itemId) => {
  if (itemType === 'subscription') return PLAY_SKU_SUBSCRIPTION_MONTHLY;
  return PLAY_SKU_ONE_TIME;
};

let _digitalGoodsService = null;
let _initAttempted = false;

export const isTwaWithPlayBilling = async () => {
  if (_initAttempted) return !!_digitalGoodsService;
  _initAttempted = true;
  if (typeof window === 'undefined' || !('getDigitalGoodsService' in window)) {
    return false;
  }
  try {
    _digitalGoodsService = await window.getDigitalGoodsService('https://play.google.com/billing');
    return !!_digitalGoodsService;
  } catch (e) {
    console.warn('[PlayBilling] getDigitalGoodsService failed, falling back to Stripe:', e);
    _digitalGoodsService = null;
    return false;
  }
};

export const getPlayProductDetails = async (skus) => {
  if (!_digitalGoodsService) return [];
  try {
    return await _digitalGoodsService.getDetails(skus);
  } catch (e) {
    console.warn('[PlayBilling] getDetails failed:', e);
    return [];
  }
};

export const listActivePlayPurchases = async () => {
  if (!_digitalGoodsService) return [];
  try {
    return await _digitalGoodsService.listPurchases();
  } catch (e) {
    console.warn('[PlayBilling] listPurchases failed:', e);
    return [];
  }
};

// Trigger a Play Billing purchase for one item. Returns { success, message, item }.
// `onPopupClosed` (optional) is fired the instant the user confirms in the
// native Google Play popup, BEFORE the backend verification call. This lets the
// caller display an "Activation in progress" loader during the verify roundtrip
// (which can take up to ~30s on cold paths).
export const purchaseViaPlayBilling = async ({ itemType, itemId, token, onPopupClosed }) => {
  if (!_digitalGoodsService) {
    return { success: false, message: 'Google Play Billing non disponible.' };
  }

  const sku = buildPlaySku(itemType, itemId);
  const methodData = [{
    supportedMethods: 'https://play.google.com/billing',
    data: { sku },
  }];
  // Play Billing ignores these totals but Payment Request API requires them.
  const details = {
    total: { label: 'Total', amount: { currency: 'EUR', value: '0' } },
  };

  let request;
  try {
    request = new PaymentRequest(methodData, details);
  } catch (e) {
    return { success: false, message: `PaymentRequest init failed: ${e.message}` };
  }

  // Helper: detect Google Play "item already owned" — happens when a previous
  // purchase of the same SKU hasn't been consumed yet (consume is async on
  // Google's side and may take 30s-2min). The exact error shape varies between
  // Chrome versions; we match by message text.
  const isAlreadyOwnedError = (err) => {
    if (!err) return false;
    const msg = `${err.name || ''} ${err.message || ''}`.toLowerCase();
    return /already.?own|item.?owned|billing_response.*7|response.?code.?7\b/i.test(msg);
  };

  // Helper: try to consume any stuck purchases of this SKU server-side so the
  // user can re-buy. Mirrors what reconcileActivePlayPurchases() does but only
  // for the SKU we care about right now.
  const consumeStuckForSku = async () => {
    if (!_digitalGoodsService) return 0;
    try {
      const purchases = await _digitalGoodsService.listPurchases();
      const stuck = purchases.filter(p => p.itemId === sku);
      let consumed = 0;
      for (const p of stuck) {
        try {
          await axios.post(
            `${API}/play-billing/verify-existing`,
            { sku: p.itemId, purchase_token: p.purchaseToken },
            { headers: { Authorization: `Bearer ${token}` }, timeout: 25000 }
          );
          consumed++;
        } catch (err) {
          console.warn('[PlayBilling] consume stuck purchase failed:', err.message);
        }
      }
      return consumed;
    } catch (err) {
      console.warn('[PlayBilling] listPurchases failed:', err.message);
      return 0;
    }
  };

  let paymentResponse;
  try {
    paymentResponse = await request.show();
  } catch (e) {
    if (e.name === 'AbortError') {
      return { success: false, cancelled: true, message: 'Achat annulé.' };
    }

    // "Item already owned" → try to consume the orphan + retry once.
    if (isAlreadyOwnedError(e)) {
      console.info('[PlayBilling] "already owned" detected, attempting to consume stuck purchase…');
      const consumed = await consumeStuckForSku();
      console.info(`[PlayBilling] consumed ${consumed} stuck purchase(s), retrying buy flow…`);

      if (consumed > 0) {
        // Build a fresh PaymentRequest (a used instance cannot be reused).
        try {
          const retryReq = new PaymentRequest(methodData, details);
          try {
            paymentResponse = await retryReq.show();
          } catch (e2) {
            if (e2.name === 'AbortError') {
              return { success: false, cancelled: true, message: 'Achat annulé.' };
            }
            if (isAlreadyOwnedError(e2)) {
              return {
                success: false,
                alreadyOwned: true,
                message: "Votre précédent achat est en cours de validation par Google. Patientez 1-2 minutes avant de faire un nouvel achat.",
              };
            }
            return { success: false, message: `Erreur Play Billing: ${e2.message}` };
          }
        } catch (eInit) {
          return { success: false, message: `PaymentRequest retry init failed: ${eInit.message}` };
        }
      } else {
        // Couldn't list/consume anything (offline? listPurchases failed?) — still
        // show the friendly message so the user knows what's happening.
        return {
          success: false,
          alreadyOwned: true,
          message: "Votre précédent achat est en cours de validation par Google. Patientez 1-2 minutes avant de faire un nouvel achat.",
        };
      }
    } else {
      return { success: false, message: `Erreur Play Billing: ${e.message}` };
    }
  }

  const purchaseToken = paymentResponse.details?.purchaseToken;
  if (!purchaseToken) {
    await paymentResponse.complete('fail');
    return { success: false, message: 'Token de paiement manquant.' };
  }

  // Popup is closed and Google accepted the payment. Signal the caller so they
  // can swap their own UI (close modals, show "Activation in progress..." loader)
  // while we run the backend verification.
  if (typeof onPopupClosed === 'function') {
    try { onPopupClosed(); } catch (_) { /* never let UX hooks break payment */ }
  }

  // Verification with auto-retry. BrokenPipe / network glitches between the
  // Android client and our backend are common right after Google confirms the
  // payment (the verify call to Google itself can take 5-20s). We retry the
  // backend call up to 2 extra times with exponential backoff before giving up.
  const isRetryableError = (err) => {
    if (!err) return false;
    // Axios sets `code` for low-level network errors. These are the ones worth
    // retrying: lost socket, server reset, broken pipe, no response received.
    const retryCodes = ['ECONNABORTED', 'ECONNRESET', 'EPIPE', 'ETIMEDOUT', 'ERR_NETWORK'];
    if (retryCodes.includes(err.code)) return true;
    // No response received at all (server killed mid-stream / 502 / 503 / 504).
    if (!err.response) return true;
    if ([502, 503, 504].includes(err.response.status)) return true;
    return false;
  };

  const sleep = (ms) => new Promise(r => setTimeout(r, ms));

  let lastErr = null;
  for (let attempt = 1; attempt <= 3; attempt++) {
    try {
      const verify = await axios.post(
        `${API}/play-billing/verify`,
        { sku, purchase_token: purchaseToken, item_type: itemType, item_id: itemId },
        {
          headers: { Authorization: `Bearer ${token}` },
          // 45s cap — matches backend's 25s verify + ~15s headroom for unlock/db.
          // If Google API is hanging beyond that, we fail visually but the
          // purchase is still in DB and will be reconciled at next app load.
          timeout: 45000,
        }
      );
      if (verify.data?.success) {
        await paymentResponse.complete('success');
        return { success: true, message: verify.data.message, item: { itemType, itemId }, attempts: attempt };
      }
      // Backend explicitly said "not valid" — don't retry, that's a real failure.
      await paymentResponse.complete('fail');
      return { success: false, message: verify.data?.message || 'Vérification échouée.', attempts: attempt };
    } catch (e) {
      lastErr = e;
      console.warn(`[PlayBilling] verify attempt ${attempt}/3 failed:`, e.code || e.message);
      if (attempt < 3 && isRetryableError(e)) {
        // Exponential backoff: 2s, 4s.
        await sleep(2000 * attempt);
        continue;
      }
      break;
    }
  }

  // All 3 attempts failed. Treat broken-pipe / timeout as RECOVERABLE because
  // the backend may have already applied the unlock (the BrokenPipe usually
  // hits AFTER unlock+DB writes, only the response transmission failed).
  if (isRetryableError(lastErr)) {
    await paymentResponse.complete('success').catch(() => {});
    return {
      success: false,
      recoverable: true,
      message: "Le paiement est confirmé mais la synchronisation avec le serveur a été interrompue. Le contenu sera débloqué automatiquement — relance l'app dans quelques secondes.",
      lastErrorCode: lastErr?.code,
    };
  }
  await paymentResponse.complete('fail').catch(() => {});
  return { success: false, message: lastErr?.response?.data?.detail || lastErr?.message || 'Vérification échouée.' };
};

// Called at app load to sync any purchases made while the user was offline or on another device.
export const reconcileActivePlayPurchases = async (token) => {
  if (!_digitalGoodsService) return { reconciled: 0 };
  const purchases = await listActivePlayPurchases();
  if (!purchases.length) return { reconciled: 0 };
  let reconciled = 0;
  for (const p of purchases) {
    try {
      const verify = await axios.post(
        `${API}/play-billing/verify-existing`,
        { sku: p.itemId, purchase_token: p.purchaseToken },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (verify.data?.success) reconciled++;
    } catch (e) {
      console.warn('[PlayBilling] reconcile existing failed:', p.itemId, e.message);
    }
  }
  return { reconciled };
};
